/**
 * Hangout message path algebra (Postgres ltree).
 *
 * Invariants:
 * 1. Persistable paths: /^[0-9]+(\.[0-9]+)*$/
 * 2. Parent of P.n is P. First message is "1" (no parent).
 * 3. Version group = assistant messages sharing the same parent path + characterId.
 *    Canonical = earliest by createdAt (tiebreak path). activeVersions[canonical] = selected.
 * 4. Continuations after an assistant are children of that assistant.
 * 5. Non-selected version nodes and all their descendants are hidden.
 * 6. outfit_change_* / non-ltree paths are local-only.
 *    Opening greetings use isGreeting + ltree chains whose first segment is variation+1
 *    (variation 0 → 1 / 1.1; variation 1 → 2 / 2.1). Non-selected roots are hidden.
 * 7. branch_param is unused; path is the sole tree key.
 *
 * Send allocation:
 * - User reply to tip T → next child of T
 * - Assistants after user U → chain: first child of U, each next = child of previous assistant
 * - Regenerate assistant at C → next sibling under parent(C); remaining regen speakers chain under the new C
 */

const escapeRegex = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const GREETING_PATH = 'greeting';

export const greetingRootSegment = (variation = 0) => {
  const parsed = Number.parseInt(String(variation ?? 0), 10);
  const safe = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  return String(safe + 1);
};

export const getPathRootSegment = (path) => {
  const normalized = normalizeMessagePath(path);
  if (!normalized || !isValidLtreePath(normalized)) return '';
  return normalized.split('.')[0] || '';
};

export const greetingChainPathAtIndex = (zeroBasedIndex, variation = 0) => {
  const index = Number.isFinite(zeroBasedIndex) ? Math.max(0, Math.trunc(zeroBasedIndex)) : 0;
  const root = greetingRootSegment(variation);
  if (index <= 0) return root;
  return `${root}.${Array.from({ length: index }, () => '1').join('.')}`;
};

export const resolveGreetingChainPath = (characterId, characters = [], variation = 0) => {
  const ids = [...new Set(
    (Array.isArray(characters) ? characters : [])
      .map((entry) => Number.parseInt(String(entry?.id ?? entry?.characterId ?? entry), 10))
      .filter((id) => Number.isFinite(id))
  )].sort((left, right) => left - right);
  const target = Number.parseInt(String(characterId), 10);
  const index = Number.isFinite(target) ? ids.indexOf(target) : -1;
  return greetingChainPathAtIndex(index >= 0 ? index : 0, variation);
};

export const normalizeMessagePath = (path) => {
  if (path === null || path === undefined) return '';
  return String(path).trim();
};

export const isRootIntegerPath = (path) => /^\d+$/.test(normalizeMessagePath(path));

export const isValidLtreePath = (path) => /^[0-9]+(\.[0-9]+)*$/.test(normalizeMessagePath(path));

export const isGreetingMessage = (message) => {
  if (!message || typeof message !== 'object') return false;
  return message.isGreeting === true || message.isInitialGreeting === true;
};

export const isLocalOnlyMessage = (message) => {
  if (!message || typeof message !== 'object') return false;
  const path = normalizeMessagePath(message.path);
  if (!path) return false;
  if (path.startsWith('outfit_change_')) return true;
  return !isValidLtreePath(path);
};

export const isPersistableMessage = (message) => {
  if (!message || typeof message !== 'object') return false;
  if (message.role !== 'user' && message.role !== 'assistant') return false;
  return !isLocalOnlyMessage(message);
};

export const isDescendantPath = (path, ancestor) => {
  const normalizedPath = normalizeMessagePath(path);
  const normalizedAncestor = normalizeMessagePath(ancestor);
  if (!normalizedPath || !normalizedAncestor) return false;
  return normalizedPath === normalizedAncestor || normalizedPath.startsWith(`${normalizedAncestor}.`);
};

export const getParentPath = (path) => {
  const normalizedPath = normalizeMessagePath(path);
  const parts = normalizedPath.split('.').filter(Boolean);
  if (parts.length <= 1) return null;
  return parts.slice(0, -1).join('.');
};

export const isPathOnPendingBranch = (path, pendingBranch) => {
  if (!pendingBranch || typeof pendingBranch !== 'object') return false;
  const normalizedPath = normalizeMessagePath(path);
  if (!normalizedPath) return false;
  const selectedPath = normalizeMessagePath(pendingBranch.selectedPath);
  const canonicalPath = normalizeMessagePath(pendingBranch.canonicalPath);
  if (selectedPath && (normalizedPath === selectedPath || isDescendantPath(normalizedPath, selectedPath))) {
    return true;
  }
  if (canonicalPath && (normalizedPath === canonicalPath || isDescendantPath(normalizedPath, canonicalPath))) {
    return true;
  }
  const pendingParent = getParentPath(selectedPath) || getParentPath(canonicalPath);
  if (!pendingParent) return false;
  return isDescendantPath(normalizedPath, pendingParent);
};

export const compareMessagePaths = (left, right) => {
  const leftPath = normalizeMessagePath(left);
  const rightPath = normalizeMessagePath(right);
  if (!leftPath && !rightPath) return 0;
  if (!leftPath) return -1;
  if (!rightPath) return 1;
  return leftPath.localeCompare(rightPath, 'en', { numeric: true });
};

const getCreatedAtValue = (message) => {
  const createdAt = message?.createdAt ?? message?.timestamp ?? 0;
  if (typeof createdAt === 'string') {
    const parsed = Date.parse(createdAt);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return Number(createdAt) || 0;
};

const getMessageCharacterId = (message) =>
  String(message?.characterId ?? message?.character_id ?? '');

const versionGroupKey = (parentPath, characterId) =>
  `${parentPath == null ? '' : parentPath}|${characterId}`;

export const sortMessagesByPath = (messages = []) =>
  [...(Array.isArray(messages) ? messages : [])].sort((left, right) => {
    const leftIsGreeting = isGreetingMessage(left);
    const rightIsGreeting = isGreetingMessage(right);
    if (leftIsGreeting !== rightIsGreeting) {
      return leftIsGreeting ? -1 : 1;
    }

    const leftPath = normalizeMessagePath(left?.path);
    const rightPath = normalizeMessagePath(right?.path);

    if (leftIsGreeting && rightIsGreeting) {
      return compareMessagePaths(leftPath, rightPath);
    }

    const leftIsLtree = isValidLtreePath(leftPath);
    const rightIsLtree = isValidLtreePath(rightPath);
    if (leftIsLtree && rightIsLtree) {
      const pathCompare = compareMessagePaths(leftPath, rightPath);
      if (pathCompare !== 0) return pathCompare;
    }
    return getCreatedAtValue(left) - getCreatedAtValue(right);
  });

const sortVersionsInGroup = (versions) => {
  versions.sort((left, right) => {
    const createdCompare = getCreatedAtValue(left) - getCreatedAtValue(right);
    if (createdCompare !== 0) return createdCompare;
    return compareMessagePaths(left?.path, right?.path);
  });
};

/**
 * Version group = assistants with the same parent path + characterId.
 * Group key remains `${canonicalPath}|${characterId}` for activeVersions lookups.
 */
const collectVersionGroupsFromSorted = (sortedMessages = []) => {
  /** @type {Map<string, { parentPath: string|null, characterId: string, versions: any[] }>} */
  const byParentChar = new Map();

  for (const message of sortedMessages) {
    if (!message || message.role !== 'assistant') continue;
    if (message.isGreeting === true || message.isInitialGreeting === true) continue;
    const path = normalizeMessagePath(message.path);
    const characterId = getMessageCharacterId(message);
    if (!path || !characterId || !isValidLtreePath(path)) continue;

    const parentPath = getParentPath(path);
    const key = versionGroupKey(parentPath, characterId);
    if (!byParentChar.has(key)) {
      byParentChar.set(key, { parentPath, characterId, versions: [] });
    }
    byParentChar.get(key).versions.push(message);
  }

  /** @type {Map<string, { key: string, canonicalPath: string, characterId: string, parentPath: string|null, versions: any[] }>} */
  const groups = new Map();
  for (const bucket of byParentChar.values()) {
    sortVersionsInGroup(bucket.versions);
    const canonicalPath = normalizeMessagePath(bucket.versions[0]?.path);
    if (!canonicalPath) continue;
    const key = `${canonicalPath}|${bucket.characterId}`;
    groups.set(key, {
      key,
      canonicalPath,
      characterId: bucket.characterId,
      parentPath: bucket.parentPath,
      versions: bucket.versions
    });
  }

  return groups;
};

export const collectVersionGroups = (messages = []) => {
  return collectVersionGroupsFromSorted(sortMessagesByPath(messages));
};

const buildGroupByPathAndCharacter = (groups) => {
  const groupByPathAndCharacter = new Map();
  for (const group of groups.values()) {
    for (const version of group.versions) {
      const versionPath = normalizeMessagePath(version.path);
      const versionCharacterId = String(version?.characterId ?? version?.character_id ?? group.characterId ?? '');
      if (!versionPath || !versionCharacterId) continue;
      groupByPathAndCharacter.set(`${versionPath}|${versionCharacterId}`, group);
    }
  }
  return groupByPathAndCharacter;
};

const collectNonSelectedVersionPaths = (groups, selection) => {
  const paths = new Set();
  for (const group of groups.values()) {
    const selectedPath = normalizeMessagePath(selection[group.canonicalPath]);
    for (const version of group.versions) {
      const versionPath = normalizeMessagePath(version.path);
      if (versionPath && versionPath !== selectedPath) paths.add(versionPath);
    }
  }
  return paths;
};

const hasStrictAncestorInSet = (path, ancestorPaths) => {
  if (!ancestorPaths.size) return false;
  const parts = String(path).split('.');
  if (parts.length < 2) return false;
  let prefix = parts[0];
  if (ancestorPaths.has(prefix)) return true;
  for (let index = 1; index < parts.length - 1; index += 1) {
    prefix = `${prefix}.${parts[index]}`;
    if (ancestorPaths.has(prefix)) return true;
  }
  return false;
};

export const resolveCanonicalPath = (path, messages = []) => {
  const normalizedPath = normalizeMessagePath(path);
  if (!normalizedPath) return '';

  const sortedMessages = sortMessagesByPath(messages);
  const target = sortedMessages.find((message) =>
    message?.role === 'assistant'
    && normalizeMessagePath(message.path) === normalizedPath
  );
  if (!target) return normalizedPath;

  const characterId = getMessageCharacterId(target);
  if (!characterId) return normalizedPath;

  const parentPath = getParentPath(normalizedPath);
  const siblings = sortedMessages.filter((message) => {
    if (message?.role !== 'assistant') return false;
    if (isGreetingMessage(message)) return false;
    const messagePath = normalizeMessagePath(message.path);
    if (!isValidLtreePath(messagePath)) return false;
    if (getMessageCharacterId(message) !== characterId) return false;
    return getParentPath(messagePath) === parentPath;
  });
  if (siblings.length === 0) return normalizedPath;

  sortVersionsInGroup(siblings);
  return normalizeMessagePath(siblings[0]?.path) || normalizedPath;
};

export const getLatestPersistableMessagePath = (messages = []) => {
  let latestPath = '';
  let latestCreatedAt = -Infinity;

  for (const message of Array.isArray(messages) ? messages : []) {
    if (!isPersistableMessage(message)) continue;
    const path = normalizeMessagePath(message?.path);
    if (!path || !isValidLtreePath(path)) continue;

    const createdAt = getCreatedAtValue(message);
    if (createdAt > latestCreatedAt) {
      latestCreatedAt = createdAt;
      latestPath = path;
      continue;
    }
    if (createdAt === latestCreatedAt && compareMessagePaths(path, latestPath) > 0) {
      latestPath = path;
    }
  }

  return latestPath;
};

/**
 * Assistants that share the same parent path as `path` (one multi-char round).
 * @param {any[]} messages
 * @param {string} path
 * @returns {any[]}
 */
export const findSameRoundAssistants = (messages = [], path = '') => {
  const normalizedPath = normalizeMessagePath(path);
  if (!normalizedPath || !isValidLtreePath(normalizedPath)) return [];
  const parent = getParentPath(normalizedPath);
  if (!parent && !isRootIntegerPath(normalizedPath)) return [];

  return sortMessagesByPath(messages).filter((message) => {
    if (!message || message.role !== 'assistant') return false;
    if (isGreetingMessage(message) || isLocalOnlyMessage(message)) return false;
    const messagePath = normalizeMessagePath(message.path);
    if (!messagePath || !isValidLtreePath(messagePath)) return false;
    return getParentPath(messagePath) === parent;
  });
};

const pickClosestVersionByCreatedAt = (versions = [], targetCreatedAt = 0) => {
  if (!Array.isArray(versions) || versions.length === 0) return null;
  let best = versions[0];
  let bestDistance = Math.abs(getCreatedAtValue(best) - targetCreatedAt);
  for (let index = 1; index < versions.length; index += 1) {
    const candidate = versions[index];
    const distance = Math.abs(getCreatedAtValue(candidate) - targetCreatedAt);
    if (distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
      continue;
    }
    if (distance === bestDistance && compareMessagePaths(candidate?.path, best?.path) > 0) {
      best = candidate;
    }
  }
  return best;
};

/**
 * For selected assistant version V, select a createdAt-matched version for every
 * same-parent assistant character (links A2↔B2 from one regenerate batch).
 * @param {any[]} messages
 * @param {string} selectedPath
 * @returns {Record<string, string>}
 */
export const selectVersionCohort = (messages = [], selectedPath = '') => {
  const list = Array.isArray(messages) ? messages : [];
  const normalizedSelected = normalizeMessagePath(selectedPath);
  /** @type {Record<string, string>} */
  const activeVersions = {};
  if (!normalizedSelected || !isValidLtreePath(normalizedSelected)) return activeVersions;

  const selectedMessage = list.find((row) =>
    row?.role === 'assistant' && normalizeMessagePath(row?.path) === normalizedSelected
  );
  if (!selectedMessage) {
    const canonical = resolveCanonicalPath(normalizedSelected, list);
    if (canonical) activeVersions[canonical] = normalizedSelected;
    return activeVersions;
  }

  const targetCreatedAt = getCreatedAtValue(selectedMessage);
  const groups = collectVersionGroups(list);
  const roundAssistants = findSameRoundAssistants(list, normalizedSelected);
  const characterIds = [...new Set(
    roundAssistants.map((message) => getMessageCharacterId(message)).filter(Boolean)
  )];

  for (const characterId of characterIds) {
    const roundForCharacter = roundAssistants.filter(
      (message) => getMessageCharacterId(message) === characterId
    );
    const samplePath = normalizeMessagePath(
      characterId === getMessageCharacterId(selectedMessage)
        ? normalizedSelected
        : (roundForCharacter[0]?.path || '')
    );
    if (!samplePath) continue;

    const canonical = resolveCanonicalPath(samplePath, list);
    if (!canonical) continue;
    const group = groups.get(`${canonical}|${characterId}`);
    if (!group || group.versions.length === 0) {
      activeVersions[canonical] = samplePath;
      continue;
    }

    if (characterId === getMessageCharacterId(selectedMessage)) {
      activeVersions[canonical] = normalizedSelected;
      continue;
    }

    const closest = pickClosestVersionByCreatedAt(group.versions, targetCreatedAt);
    const closestPath = normalizeMessagePath(closest?.path);
    if (closestPath) activeVersions[canonical] = closestPath;
  }

  return activeVersions;
};

/**
 * Walk tip ancestors and select each assistant version on that lineage.
 * Also cohort-selects earlier same-round speakers under each tip assistant's parent.
 * @param {any[]} messages
 * @param {string} tipPath
 * @returns {Record<string, string>}
 */
export const buildActiveVersionsForTipPath = (messages = [], tipPath = '') => {
  const list = Array.isArray(messages) ? messages : [];
  /** @type {Record<string, string>} */
  const activeVersions = {};
  let current = normalizeMessagePath(tipPath);

  while (current) {
    const node = list.find((row) => normalizeMessagePath(row?.path) === current);
    if (node?.role === 'assistant') {
      const cohort = selectVersionCohort(list, current);
      Object.assign(activeVersions, cohort);
      const canonical = resolveCanonicalPath(current, list);
      if (canonical) activeVersions[canonical] = current;
    }
    current = getParentPath(current);
  }

  return activeVersions;
};

export const resolveActiveVersions = (messages = [], activeVersions = {}, precomputedGroups = null) => {
  const groups = precomputedGroups || collectVersionGroups(messages);
  const tipDefaults = buildActiveVersionsForTipPath(
    messages,
    getLatestPersistableMessagePath(messages)
  );
  /** @type {Record<string, string>} */
  const resolved = {};

  for (const group of groups.values()) {
    const preferred = normalizeMessagePath(activeVersions?.[group.canonicalPath]);
    const hasPreferred = group.versions.some((version) => normalizeMessagePath(version.path) === preferred);
    if (hasPreferred) {
      resolved[group.canonicalPath] = preferred;
      continue;
    }

    const tipPreferred = normalizeMessagePath(tipDefaults?.[group.canonicalPath]);
    const hasTipPreferred = tipPreferred
      && group.versions.some((version) => normalizeMessagePath(version.path) === tipPreferred);
    if (hasTipPreferred) {
      resolved[group.canonicalPath] = tipPreferred;
      continue;
    }

    const latest = group.versions[group.versions.length - 1];
    resolved[group.canonicalPath] = normalizeMessagePath(latest?.path) || group.canonicalPath;
  }

  return resolved;
};

const isStrictDescendantPath = (path, ancestor) => {
  const normalizedPath = normalizeMessagePath(path);
  const normalizedAncestor = normalizeMessagePath(ancestor);
  if (!normalizedPath || !normalizedAncestor) return false;
  return normalizedPath.startsWith(`${normalizedAncestor}.`);
};

const isUnderNonSelectedVersion = (path, groups, selection) => {
  const normalizedPath = normalizeMessagePath(path);
  for (const group of groups.values()) {
    const selectedPath = normalizeMessagePath(selection[group.canonicalPath]);
    for (const version of group.versions) {
      const versionPath = normalizeMessagePath(version.path);
      if (!versionPath || versionPath === selectedPath) continue;
      if (isStrictDescendantPath(normalizedPath, versionPath)) return true;
    }
  }
  return false;
};

const shouldHidePendingOldTipDescendant = (path, pendingBranch) => {
  const pendingSelectedPath = normalizeMessagePath(pendingBranch?.selectedPath);
  if (!pendingSelectedPath) return false;
  const normalizedPath = normalizeMessagePath(path);
  if (!normalizedPath || normalizedPath === pendingSelectedPath) return false;
  return isDescendantPath(normalizedPath, pendingSelectedPath);
};

const shouldHidePendingHiddenPath = (path, pendingBranch) => {
  const hiddenPaths = pendingBranch?.hiddenPaths;
  if (!Array.isArray(hiddenPaths) || hiddenPaths.length === 0) return false;
  const normalizedPath = normalizeMessagePath(path);
  if (!normalizedPath) return false;
  return hiddenPaths.some((hiddenPath) => {
    const normalizedHidden = normalizeMessagePath(hiddenPath);
    if (!normalizedHidden) return false;
    return isDescendantPath(normalizedPath, normalizedHidden);
  });
};

const isAncestorOfTip = (path, tipPath) => {
  const normalizedPath = normalizeMessagePath(path);
  const normalizedTip = normalizeMessagePath(tipPath);
  if (!normalizedPath || !normalizedTip) return false;
  if (normalizedPath === normalizedTip) return true;
  return normalizedTip.startsWith(`${normalizedPath}.`);
};

export const getVisibleMessages = (messages = [], activeVersions = {}, options = {}) => {
  const sortedMessages = sortMessagesByPath(messages);
  const groups = collectVersionGroupsFromSorted(sortedMessages);
  const selection = resolveActiveVersions(sortedMessages, activeVersions, groups);
  const pendingBranch = options?.pendingBranch && typeof options.pendingBranch === 'object'
    ? options.pendingBranch
    : null;
  const branchTipPath = normalizeMessagePath(options?.branchTipPath);
  const hasBranchTip = Boolean(branchTipPath && isValidLtreePath(branchTipPath));
  const parsedGreetingVariation = Number.parseInt(String(options?.greetingVariation ?? ''), 10);
  const greetingRoot = Number.isFinite(parsedGreetingVariation) && parsedGreetingVariation >= 0
    ? greetingRootSegment(parsedGreetingVariation)
    : null;
  const effectiveBranchTip = hasBranchTip && (!greetingRoot || getPathRootSegment(branchTipPath) === greetingRoot)
    ? branchTipPath
    : '';
  const hasEffectiveBranchTip = Boolean(effectiveBranchTip);
  const groupByPathAndCharacter = buildGroupByPathAndCharacter(groups);
  const nonSelectedVersionPaths = collectNonSelectedVersionPaths(groups, selection);

  return sortedMessages.filter((message) => {
    if (!message) return false;
    if (message.isJoinMessage === true || message.isLeaveMessage === true) return true;
    const path = normalizeMessagePath(message.path);
    if (!path) return true;

    if (greetingRoot && isValidLtreePath(path) && getPathRootSegment(path) !== greetingRoot) return false;
    if (shouldHidePendingHiddenPath(path, pendingBranch)) return false;
    if (shouldHidePendingOldTipDescendant(path, pendingBranch)) return false;
    if (hasEffectiveBranchTip) {
      const onAllowlist = isAncestorOfTip(path, effectiveBranchTip)
        || isStrictDescendantPath(path, effectiveBranchTip);
      if (!onAllowlist) return false;
    }
    if (hasStrictAncestorInSet(path, nonSelectedVersionPaths)) return false;

    const characterId = getMessageCharacterId(message);
    if (message.role === 'assistant' && characterId) {
      const group = groupByPathAndCharacter.get(`${path}|${characterId}`);
      if (group && group.versions.length > 0) {
        return path === normalizeMessagePath(selection[group.canonicalPath]);
      }
    }

    return true;
  });
};

const isRoundAssistantMessage = (message) => {
  if (!message || message.role !== 'assistant') return false;
  if (isGreetingMessage(message) || isLocalOnlyMessage(message)) return false;
  return isValidLtreePath(message.path);
};

const getDirectChildMessages = (messages, parentPath) => {
  const normalizedParent = parentPath == null ? null : normalizeMessagePath(parentPath);
  return sortMessagesByPath(messages).filter((message) => {
    const path = normalizeMessagePath(message?.path);
    if (!path || !isValidLtreePath(path)) return false;
    return getParentPath(path) === normalizedParent;
  });
};

/**
 * Split a multi-character round at the branching assistant.
 * Chain encoding: kept = same-turn assistant ancestors; regen = branch + assistant-child spine.
 */
export const getRestOfTurnAssistants = (messages = [], branchPath) => {
  const list = Array.isArray(messages) ? messages : [];
  const normalizedBranch = normalizeMessagePath(branchPath);
  if (!normalizedBranch || !isValidLtreePath(normalizedBranch)) {
    return { kept: [], regen: [] };
  }

  const branchMessage = list.find((message) =>
    normalizeMessagePath(message?.path) === normalizedBranch
  );
  if (!isRoundAssistantMessage(branchMessage)) {
    return { kept: [], regen: [] };
  }

  const byPath = new Map();
  for (const message of list) {
    const path = normalizeMessagePath(message?.path);
    if (path) byPath.set(path, message);
  }

  const kept = [];
  let ancestorPath = getParentPath(normalizedBranch);
  while (ancestorPath) {
    const ancestor = byPath.get(ancestorPath);
    if (!isRoundAssistantMessage(ancestor)) break;
    kept.unshift(ancestor);
    ancestorPath = getParentPath(ancestorPath);
  }

  const regen = [];
  const seen = new Set();
  let cursor = branchMessage;
  while (isRoundAssistantMessage(cursor)) {
    const cursorPath = normalizeMessagePath(cursor.path);
    if (seen.has(cursorPath)) break;
    seen.add(cursorPath);
    regen.push(cursor);
    const nextAssistant = getDirectChildMessages(list, cursorPath)
      .find((child) => isRoundAssistantMessage(child));
    cursor = nextAssistant || null;
  }

  return { kept, regen };
};

/**
 * Cut conversation history for regenerate prompts.
 * Keeps strict ancestors of the regenerate tip, plus earlier same-round assistants (kept).
 * Drops the tip / regen tail and all of their version siblings.
 */
export const filterHistoryForRegenerate = (messages = [], {
  path = null,
  characterId = null,
  canonicalPath = null,
  restOfTurn = null
} = {}) => {
  const cutPath = normalizeMessagePath(path);
  const normalizedCharacterId = String(characterId ?? '');
  if (!cutPath) return [];

  const sortedMessages = sortMessagesByPath(messages);
  const groups = collectVersionGroupsFromSorted(sortedMessages);

  const regenList = Array.isArray(restOfTurn?.regen) && restOfTurn.regen.length > 0
    ? restOfTurn.regen
    : [{ path: cutPath, characterId: normalizedCharacterId || null, canonicalPath }];

  const excludedPaths = new Set();
  for (const regenMessage of regenList) {
    const regenPath = normalizeMessagePath(regenMessage?.path);
    const regenCharacterId = String(regenMessage?.characterId ?? '');
    const regenCanonical = normalizeMessagePath(regenMessage?.canonicalPath)
      || (regenPath ? resolveCanonicalPath(regenPath, sortedMessages) : null);
    if (regenPath) excludedPaths.add(regenPath);
    if (regenCanonical) excludedPaths.add(regenCanonical);
    if (regenCharacterId && regenCanonical) {
      const group = groups.get(`${regenCanonical}|${regenCharacterId}`);
      for (const version of group?.versions || []) {
        const versionPath = normalizeMessagePath(version?.path);
        if (versionPath) excludedPaths.add(versionPath);
      }
    }
  }

  const keptPathSet = new Set(
    (Array.isArray(restOfTurn?.kept) ? restOfTurn.kept : [])
      .map((message) => normalizeMessagePath(message?.path))
      .filter(Boolean)
  );

  return sortedMessages.filter((message) => {
    if (!message) return false;
    const messagePath = normalizeMessagePath(message?.path);
    if (!messagePath || !isValidLtreePath(messagePath)) return false;
    if (excludedPaths.has(messagePath)) return false;
    if (keptPathSet.has(messagePath)) return true;
    // Strict ancestors of the regenerate tip.
    return cutPath.startsWith(`${messagePath}.`);
  });
};

export const filterHistoryForContinue = (messages = [], continuePath) => {
  const cutPath = normalizeMessagePath(continuePath);
  if (!cutPath) return [];
  return sortMessagesByPath(messages).filter((message) => {
    const messagePath = normalizeMessagePath(message?.path);
    if (!messagePath || !isValidLtreePath(messagePath)) return false;
    return cutPath.startsWith(`${messagePath}.`);
  });
};

export const buildVersionMetaIndex = (messages = [], activeVersions = {}) => {
  const sortedMessages = sortMessagesByPath(messages);
  const groups = collectVersionGroupsFromSorted(sortedMessages);
  const selection = resolveActiveVersions(sortedMessages, activeVersions, groups);
  const groupByPathAndCharacter = buildGroupByPathAndCharacter(groups);
  return { sortedMessages, groups, selection, groupByPathAndCharacter };
};

export const getVersionMetaFromIndex = (index, path, characterId) => {
  const normalizedPath = normalizeMessagePath(path);
  const normalizedCharacterId = String(characterId ?? '');
  if (!normalizedPath || !normalizedCharacterId) {
    return { canonicalPath: normalizedPath, versions: [], index: 0, total: 0, selectedPath: normalizedPath };
  }

  const group = index?.groupByPathAndCharacter?.get(`${normalizedPath}|${normalizedCharacterId}`);
  const canonicalPath = group?.canonicalPath || normalizedPath;
  const versions = group?.versions || [];
  const selectedPath = index?.selection?.[canonicalPath] || normalizedPath;
  const foundIndex = Math.max(0, versions.findIndex((version) => normalizeMessagePath(version.path) === selectedPath));

  return {
    canonicalPath,
    versions,
    index: versions.length ? foundIndex + 1 : 1,
    total: versions.length || 1,
    selectedPath
  };
};

export const getVersionMetaForPath = (messages = [], path, characterId, activeVersions = {}) => {
  return getVersionMetaFromIndex(buildVersionMetaIndex(messages, activeVersions), path, characterId);
};

export const getNextChildPath = (parentPath, messages = []) => {
  const normalizedParent = normalizeMessagePath(parentPath);
  if (!normalizedParent || !isValidLtreePath(normalizedParent)) return '1';

  const childPattern = new RegExp(`^${escapeRegex(normalizedParent)}\\.(\\d+)$`);
  let maxIndex = 0;
  for (const message of Array.isArray(messages) ? messages : []) {
    const match = childPattern.exec(normalizeMessagePath(message?.path));
    if (!match) continue;
    const value = Number.parseInt(match[1], 10);
    if (Number.isFinite(value) && value > maxIndex) maxIndex = value;
  }
  return `${normalizedParent}.${maxIndex + 1}`;
};

export const getNextPersistableRootPath = (messages = [], activeVersions = {}, options = {}) => {
  const list = Array.isArray(messages) ? messages : [];
  const hasPersistable = list.some((message) =>
    isPersistableMessage(message) && isValidLtreePath(message?.path)
  );
  if (!hasPersistable) return '1';

  const visible = getVisibleMessages(list, activeVersions, {
    pendingBranch: options?.pendingBranch || null,
    branchTipPath: options?.branchTipPath || null,
    greetingVariation: options?.greetingVariation
  });
  let tip = getActiveBranchTipPath(visible);
  if (!tip && Number.isFinite(Number.parseInt(String(options?.greetingVariation ?? ''), 10))) {
    const root = greetingRootSegment(options.greetingVariation);
    tip = getActiveBranchTipPath(
      list.filter((message) => getPathRootSegment(message?.path) === root)
    );
  }
  if (!tip) return '1';
  return getNextChildPath(tip, list);
};

export const getActiveBranchTipPath = (visibleMessages = []) => {
  const sorted = sortMessagesByPath(visibleMessages);
  for (let index = sorted.length - 1; index >= 0; index -= 1) {
    const path = normalizeMessagePath(sorted[index]?.path);
    if (path && isValidLtreePath(path)) return path;
  }
  return null;
};

export const buildAssistantChainPaths = (parentPath, assistantCount = 1, messages = []) => {
  const plannedAssistants = Math.max(0, Number(assistantCount) || 0);
  const planned = [...(Array.isArray(messages) ? messages : [])];
  const assistantPaths = [];
  let parentForNext = normalizeMessagePath(parentPath);
  if (!parentForNext || !isValidLtreePath(parentForNext)) return assistantPaths;

  for (let index = 0; index < plannedAssistants; index += 1) {
    const next = getNextChildPath(parentForNext, planned);
    assistantPaths.push(next);
    planned.push({ path: next });
    parentForNext = next;
  }
  return assistantPaths;
};

export const buildRegeneratePathPlan = (messages = [], branchPath = '', regenCount = 1) => {
  const list = Array.isArray(messages) ? messages : [];
  const normalizedBranch = normalizeMessagePath(branchPath);
  const parentPath = getParentPath(normalizedBranch);
  const plannedCount = Math.max(1, Number(regenCount) || 1);
  if (!normalizedBranch || !isValidLtreePath(normalizedBranch) || !parentPath) {
    return { branchPath: normalizedBranch, assistantPaths: [] };
  }

  const assistantPaths = buildAssistantChainPaths(parentPath, plannedCount, list);
  return {
    branchPath: normalizedBranch,
    assistantPaths
  };
};

/**
 * Plan paths for a normal send: user = child of tip; assistants = chain under that user.
 * Always returns a plan (including mainline). Empty tree → user "1", assistants under it.
 */
export const buildContinuationPathPlan = (messages = [], activeVersions = {}, assistantCount = 1, options = {}) => {
  const list = Array.isArray(messages) ? messages : [];
  const visibleMessages = getVisibleMessages(list, activeVersions, {
    pendingBranch: options?.pendingBranch || null,
    branchTipPath: options?.branchTipPath || null,
    greetingVariation: options?.greetingVariation
  });
  let tipPath = getActiveBranchTipPath(visibleMessages);
  if (!tipPath && Number.isFinite(Number.parseInt(String(options?.greetingVariation ?? ''), 10))) {
    const root = greetingRootSegment(options.greetingVariation);
    tipPath = getActiveBranchTipPath(
      list.filter((message) => getPathRootSegment(message?.path) === root)
    );
  }
  const plannedAssistants = Math.max(1, Number(assistantCount) || 1);

  let userPath;
  if (!tipPath) {
    userPath = '1';
  } else {
    userPath = getNextChildPath(tipPath, list);
  }

  const assistantPaths = buildAssistantChainPaths(
    userPath,
    plannedAssistants,
    [...list, { path: userPath, role: 'user' }]
  );

  return {
    tipPath: tipPath || null,
    userPath,
    assistantPaths
  };
};

export const hasSiblingAssistantRoundParents = (messages = []) => {
  const characterIdsByParent = new Map();
  for (const message of messages) {
    if (!isRoundAssistantMessage(message)) continue;
    const characterId = getMessageCharacterId(message);
    if (!characterId) continue;
    const parent = getParentPath(normalizeMessagePath(message.path));
    const key = parent == null ? '' : parent;
    if (!characterIdsByParent.has(key)) characterIdsByParent.set(key, new Set());
    const ids = characterIdsByParent.get(key);
    ids.add(characterId);
    if (ids.size >= 2) return true;
  }
  return false;
};

export const getPrecedingUserMessage = (messages = [], assistantPath) => {
  const list = Array.isArray(messages) ? messages : [];
  let cursor = getParentPath(normalizeMessagePath(assistantPath));
  while (cursor) {
    const parentMessage = list.find((message) =>
      normalizeMessagePath(message?.path) === cursor
    );
    if (parentMessage?.role === 'user') return parentMessage;
    cursor = getParentPath(cursor);
  }
  return null;
};

export const getRoundAssistantMessages = (messages = [], targetPath) => {
  const { kept, regen } = getRestOfTurnAssistants(messages, targetPath);
  return [...kept, ...regen];
};

export const isValidMilitaryTime = (value) => {
  if (!Number.isFinite(value)) return false;
  const total = Math.trunc(value);
  return total >= 0
    && total <= 2359
    && (total % 100) < 60
    && Math.floor(total / 100) <= 23;
};

const parseMessageTimeMilitary = (value) => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    const military = Math.trunc(value);
    return isValidMilitaryTime(military) ? military : null;
  }
  const text = String(value).trim();
  if (/^\d+$/.test(text)) {
    const military = Number.parseInt(text, 10);
    return isValidMilitaryTime(military) ? military : null;
  }
  const match = text.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hours = Number.parseInt(match[1], 10);
  const mins = Number.parseInt(match[2], 10);
  if (hours > 23 || mins > 59) return null;
  return hours * 100 + mins;
};

export const formatMilitaryAsClock = (military) => {
  if (!isValidMilitaryTime(Number(military))) return null;
  const total = Math.trunc(Number(military));
  const hours = Math.floor(total / 100);
  const mins = total % 100;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

export const militaryToTimeOfDaySlot = (military) => {
  if (!isValidMilitaryTime(Number(military))) return null;
  const hour = Math.floor(Math.trunc(Number(military)) / 100);
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
};

/** Maps military HHMM to BackgroundService slots: day / sunset / night. */
export const militaryToBackgroundTimeSlot = (military) => {
  if (!isValidMilitaryTime(Number(military))) return null;
  const hour = Math.floor(Math.trunc(Number(military)) / 100);
  if (hour >= 0 && hour <= 5) return 'night';
  if (hour >= 6 && hour <= 7) return 'sunset';
  if (hour >= 8 && hour <= 17) return 'day';
  if (hour >= 18 && hour <= 19) return 'sunset';
  return 'night';
};

const BACKGROUND_TIME_ALIASES = {
  morning: 'day',
  afternoon: 'day',
  evening: 'sunset',
  sunrise: 'sunset',
  day: 'day',
  sunset: 'sunset',
  night: 'night'
};

export const resolveBackgroundTimeSlot = (slotOrMilitary) => {
  if (slotOrMilitary == null || slotOrMilitary === '') return null;
  if (typeof slotOrMilitary === 'number' || /^\d+$/.test(String(slotOrMilitary).trim())) {
    return militaryToBackgroundTimeSlot(Number(slotOrMilitary));
  }
  const normalized = String(slotOrMilitary).trim().toLowerCase();
  return BACKGROUND_TIME_ALIASES[normalized] || null;
};

const resolveMessageLocationCandidate = (message) => {
  const stateDiff = message?.stateDiff ?? message?.state_diff ?? null;
  return message?.locationId
    ?? message?.location_id
    ?? stateDiff?.locId
    ?? stateDiff?.location_id
    ?? stateDiff?.location
    ?? stateDiff?.loc
    ?? stateDiff?.loc_id
    ?? null;
};

export const resolveHistoricalSceneFromMessages = (messages = [], targetPath) => {
  const sortedMessages = sortMessagesByPath(messages);
  const normalizedTarget = normalizeMessagePath(targetPath);
  const targetIndex = sortedMessages.findIndex((message) =>
    normalizeMessagePath(message?.path) === normalizedTarget
  );
  if (targetIndex < 0) {
    return { locationId: null, time: null, currentTime: null, timeOfDay: null };
  }

  let locationId = null;
  let time = null;
  for (let index = targetIndex; index >= 0; index -= 1) {
    const message = sortedMessages[index];
    if (locationId == null) {
      const candidate = resolveMessageLocationCandidate(message);
      const parsed = Number.parseInt(String(candidate ?? ''), 10);
      if (Number.isFinite(parsed) && parsed >= 0) locationId = parsed;
    }
    if (time == null) {
      const stateDiff = message?.stateDiff ?? message?.state_diff ?? null;
      time = parseMessageTimeMilitary(
        message?.time
        ?? message?.currentTime
        ?? stateDiff?.current_time
        ?? stateDiff?.time
      );
    }
    if (locationId != null && time != null) break;
  }

  return {
    locationId,
    time,
    currentTime: formatMilitaryAsClock(time),
    timeOfDay: militaryToBackgroundTimeSlot(time)
  };
};

export const parsePositiveLocationId = (value) => {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const resolveTurnLocationId = ({
  requestLocation = null,
  history = [],
  targetPath = null,
  sessionLocation = null
} = {}) => {
  const fromRequest = parsePositiveLocationId(requestLocation);
  if (fromRequest != null) return fromRequest;
  const scenePath = targetPath
    || (Array.isArray(history) && history.length > 0 ? history[history.length - 1]?.path : null);
  if (scenePath) {
    const fromHistory = parsePositiveLocationId(
      resolveHistoricalSceneFromMessages(history, scenePath).locationId
    );
    if (fromHistory != null) return fromHistory;
  }
  return parsePositiveLocationId(sessionLocation);
};

export const resolvePersistLocationId = ({
  movedLocation = null,
  turnLocation = null,
  sessionLocation = null
} = {}) => parsePositiveLocationId(movedLocation)
  ?? parsePositiveLocationId(turnLocation)
  ?? parsePositiveLocationId(sessionLocation);

/**
 * Rebase local persistable message paths onto DB paths (order-matched),
 * keeping local-only rows (greeting / outfit_change_*).
 */
export const reconcileLocalMessagesWithDbMessages = (localMessages = [], dbMessages = []) => {
  const localList = Array.isArray(localMessages) ? localMessages : [];
  const dbList = Array.isArray(dbMessages) ? dbMessages : [];

  const localOnly = [];
  const localPersistable = [];
  for (const message of localList) {
    if (!message) continue;
    if (isLocalOnlyMessage(message)) {
      localOnly.push(message);
      continue;
    }
    if (message.role === 'user' || message.role === 'assistant') {
      localPersistable.push(message);
    }
  }

  const dbPersistable = dbList.filter((message) =>
    message && (message.role === 'user' || message.role === 'assistant') && isValidLtreePath(message.path)
  );

  const rebasedPersistable = [];
  const pairCount = Math.max(localPersistable.length, dbPersistable.length);
  for (let index = 0; index < pairCount; index += 1) {
    const localMessage = localPersistable[index] || null;
    const dbMessage = dbPersistable[index] || null;
    if (dbMessage && localMessage) {
      rebasedPersistable.push({
        ...localMessage,
        ...dbMessage,
        content: dbMessage.content ?? localMessage.content,
        path: normalizeMessagePath(dbMessage.path),
        role: dbMessage.role || localMessage.role,
        characterId: dbMessage.characterId ?? dbMessage.character_id ?? localMessage.characterId ?? null,
        characterName: dbMessage.characterName ?? dbMessage.character_name ?? localMessage.characterName ?? null,
        outfitId: dbMessage.outfitId ?? dbMessage.outfit_id ?? localMessage.outfitId ?? null,
        actionId: dbMessage.actionId ?? dbMessage.action_id ?? localMessage.actionId ?? null,
        createdAt: dbMessage.createdAt ?? dbMessage.created_at ?? localMessage.createdAt,
        isGreeting: dbMessage.isGreeting === true || localMessage.isGreeting === true,
        isInitialGreeting: dbMessage.isInitialGreeting === true || localMessage.isInitialGreeting === true
      });
      continue;
    }
    if (dbMessage) {
      rebasedPersistable.push({
        role: dbMessage.role,
        content: dbMessage.content,
        path: normalizeMessagePath(dbMessage.path),
        characterId: dbMessage.characterId ?? dbMessage.character_id ?? null,
        characterName: dbMessage.characterName ?? dbMessage.character_name ?? null,
        outfitId: dbMessage.outfitId ?? dbMessage.outfit_id ?? null,
        actionId: dbMessage.actionId ?? dbMessage.action_id ?? null,
        createdAt: dbMessage.createdAt ?? dbMessage.created_at ?? Date.now(),
        isGreeting: dbMessage.isGreeting === true,
        isInitialGreeting: dbMessage.isInitialGreeting === true,
        ...(dbMessage.persistedRemote === true ? { persistedRemote: true } : {})
      });
      continue;
    }
    if (localMessage) {
      rebasedPersistable.push(localMessage);
    }
  }

  return sortMessagesByPath([...localOnly, ...rebasedPersistable]);
};

export const findReconciledPathForLocalMessage = (localMessages = [], dbMessages = [], localPath) => {
  const normalizedLocalPath = normalizeMessagePath(localPath);
  if (!normalizedLocalPath) return null;
  const reconciled = reconcileLocalMessagesWithDbMessages(localMessages, dbMessages);
  const localIndex = (Array.isArray(localMessages) ? localMessages : [])
    .findIndex((message) => normalizeMessagePath(message?.path) === normalizedLocalPath);
  if (localIndex < 0) return null;

  const localMessage = localMessages[localIndex];
  if (isLocalOnlyMessage(localMessage)) return null;

  const persistableBefore = (Array.isArray(localMessages) ? localMessages : [])
    .slice(0, localIndex + 1)
    .filter((message) => isPersistableMessage(message) || (
      !isLocalOnlyMessage(message) && (message?.role === 'user' || message?.role === 'assistant')
    ));
  const targetOrdinal = persistableBefore.length - 1;
  const reconciledPersistable = reconciled.filter((message) => isValidLtreePath(message?.path));
  const matched = reconciledPersistable[targetOrdinal];
  return matched ? normalizeMessagePath(matched.path) : null;
};

const normalizeComparableContent = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

export const contentsLooselyMatch = (left, right, prefixLength = 200) => {
  const leftNormalized = normalizeComparableContent(left);
  const rightNormalized = normalizeComparableContent(right);
  if (!leftNormalized || !rightNormalized) return false;
  const leftPrefix = leftNormalized.slice(0, prefixLength);
  const rightPrefix = rightNormalized.slice(0, prefixLength);
  return leftPrefix === rightPrefix
    || leftNormalized.startsWith(rightPrefix)
    || rightNormalized.startsWith(leftPrefix);
};

const listPersistableAssistantsForCharacter = (messages = [], characterId) => {
  const normalizedCharacterId = String(characterId ?? '');
  if (!normalizedCharacterId) return [];
  return (Array.isArray(messages) ? messages : []).filter((message) => {
    if (!message || message.role !== 'assistant') return false;
    if (isGreetingMessage(message) || isLocalOnlyMessage(message)) return false;
    if (String(message.characterId ?? message.character_id ?? '') !== normalizedCharacterId) return false;
    return true;
  });
};

const listDbAssistantsForCharacter = (messages = [], characterId) => {
  const normalizedCharacterId = String(characterId ?? '');
  if (!normalizedCharacterId) return [];
  return (Array.isArray(messages) ? messages : []).filter((message) => {
    if (!message || message.role !== 'assistant') return false;
    if (!isValidLtreePath(message.path)) return false;
    if (String(message.characterId ?? message.character_id ?? '') !== normalizedCharacterId) return false;
    return true;
  });
};

export const resolveDbPathForLocalAssistant = ({
  localMessages = [],
  dbMessages = [],
  localPath,
  characterId,
  content = null
} = {}) => {
  const normalizedLocalPath = normalizeMessagePath(localPath);
  const normalizedCharacterId = String(characterId ?? '');
  if (!normalizedLocalPath || !normalizedCharacterId) return null;

  const localAssistants = listPersistableAssistantsForCharacter(localMessages, normalizedCharacterId);
  const localTarget = localAssistants.find((message) =>
    normalizeMessagePath(message?.path) === normalizedLocalPath
  ) || (Array.isArray(localMessages) ? localMessages : []).find((message) =>
    normalizeMessagePath(message?.path) === normalizedLocalPath
    && String(message?.characterId ?? message?.character_id ?? '') === normalizedCharacterId
  );
  if (!localTarget || isGreetingMessage(localTarget)) return null;

  const targetContent = content != null ? content : localTarget.content;
  const dbAssistants = listDbAssistantsForCharacter(dbMessages, normalizedCharacterId);
  if (dbAssistants.length === 0) return null;

  const contentMatch = dbAssistants.find((message) =>
    contentsLooselyMatch(message?.content ?? message?.message, targetContent)
  );
  const contentPath = contentMatch ? normalizeMessagePath(contentMatch.path) : null;

  const localOrdinal = localAssistants.findIndex((message) =>
    normalizeMessagePath(message?.path) === normalizedLocalPath
  );
  const ordinalMatch = localOrdinal >= 0 ? dbAssistants[localOrdinal] : null;
  const ordinalPath = ordinalMatch ? normalizeMessagePath(ordinalMatch.path) : null;

  if (contentPath && ordinalPath && contentPath !== ordinalPath) return contentPath;
  if (contentPath) return contentPath;
  if (ordinalPath) return ordinalPath;
  return null;
};

export const resolveDbPathForLocalMessage = ({
  localMessages = [],
  dbMessages = [],
  localPath,
  characterId = null,
  role = null,
  content = null
} = {}) => {
  const normalizedLocalPath = normalizeMessagePath(localPath);
  if (!normalizedLocalPath) return null;

  const localTarget = (Array.isArray(localMessages) ? localMessages : []).find((message) =>
    normalizeMessagePath(message?.path) === normalizedLocalPath
  );
  if (!localTarget || isLocalOnlyMessage(localTarget) || isGreetingMessage(localTarget)) return null;

  const targetRole = role || localTarget.role;
  if (targetRole === 'assistant') {
    return resolveDbPathForLocalAssistant({
      localMessages,
      dbMessages,
      localPath: normalizedLocalPath,
      characterId: characterId ?? localTarget.characterId ?? localTarget.character_id,
      content: content != null ? content : localTarget.content
    });
  }

  const targetContent = content != null ? content : localTarget.content;
  const localUsers = (Array.isArray(localMessages) ? localMessages : []).filter((message) =>
    message?.role === 'user' && !isLocalOnlyMessage(message) && !isGreetingMessage(message)
  );
  const dbUsers = (Array.isArray(dbMessages) ? dbMessages : []).filter((message) =>
    message?.role === 'user' && isValidLtreePath(message.path)
  );

  const contentMatch = dbUsers.find((message) =>
    contentsLooselyMatch(message?.content ?? message?.message, targetContent)
  );
  if (contentMatch) return normalizeMessagePath(contentMatch.path);

  const localOrdinal = localUsers.findIndex((message) =>
    normalizeMessagePath(message?.path) === normalizedLocalPath
  );
  if (localOrdinal >= 0 && dbUsers[localOrdinal]) {
    return normalizeMessagePath(dbUsers[localOrdinal].path);
  }
  return null;
};

